// Slice 2C2A · Closeout Defaults page — one shared family card. (Slice
// 2C2B adds per-family localStorage draft persistence: the card owns
// draft handling, but this page forwards the authenticated user id, a
// per-family baseline, and a refetch callback so the card can pre-check
// the server before saving and can restore the latest saved values on
// discard.)
import { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import DocumentDefaultsFamilyCard from "@/components/docs/DocumentDefaultsFamilyCard";
import {
  CLOSEOUT_FAMILY,
  CLOSEOUT_DEFAULTS_KEYS,
} from "@/components/docs/documentDefaultsFamilies";

const emptyForm = CLOSEOUT_DEFAULTS_KEYS.reduce((acc, k) => {
  acc[k] = "";
  return acc;
}, {});

const CloseoutDefaults = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const userId = user?.id || "";
  const [values, setValues] = useState(emptyForm);
  const [baseline, setBaseline] = useState(emptyForm);
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/closeout-defaults")
      .then((r) => {
        if (cancelled) return;
        const { updated_at, ...rest } = r.data || {};
        const next = { ...emptyForm, ...rest };
        setValues(next);
        setBaseline(next);
        setBaselineUpdatedAt(updated_at || "");
      })
      .catch(() => toast.error(t("closeout_defaults_page.toast_failed")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const onChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const replaceFamilyValues = useCallback((familyValues) => {
    setValues((prev) => ({ ...prev, ...familyValues }));
  }, []);

  const replaceFamilyBaseline = useCallback((familyValues, updatedAt) => {
    setBaseline((prev) => ({ ...prev, ...familyValues }));
    if (updatedAt) setBaselineUpdatedAt(updatedAt);
  }, []);

  // Slice 2C2B blocker fix: refetch is READ-ONLY. It never mutates the
  // global baseline; only sanctioned card handlers do that via
  // onBaselineReplace. Prevents any cross-tab save from cascading a
  // silent value replacement into this tab's dirty family.
  const refetchFamily = useCallback(async () => {
    const r = await api.get("/closeout-defaults");
    const { updated_at, ...rest } = r.data || {};
    const merged = { ...emptyForm, ...rest };
    const familySlice = {};
    for (const f of CLOSEOUT_FAMILY.fields) familySlice[f.key] = merged[f.key] || "";
    return { values: familySlice, updated_at: updated_at || "" };
  }, []);

  return (
    <DashboardLayout
      title={t("closeout_defaults_page.title")}
      subtitle={t("closeout_defaults_page.subtitle")}
    >
      <div className="max-w-4xl space-y-6" data-testid="closeout-defaults-page">
        {loading ? (
          <div className="p-6 text-sm text-[var(--jc-text-soft)]">
            {t("closeout_defaults_page.saving")}
          </div>
        ) : (
          <DocumentDefaultsFamilyCard
            familyKey={CLOSEOUT_FAMILY.key}
            title={CLOSEOUT_FAMILY.title}
            purpose={CLOSEOUT_FAMILY.purpose}
            snapshotNote={CLOSEOUT_FAMILY.snapshotNote}
            fields={CLOSEOUT_FAMILY.fields}
            values={values}
            onChange={onChange}
            endpoint={CLOSEOUT_FAMILY.endpoint}
            saveButtonLabel={CLOSEOUT_FAMILY.saveButtonLabel}
            successMessage={CLOSEOUT_FAMILY.successMessage}
            userId={userId}
            baseline={baseline}
            baselineUpdatedAt={baselineUpdatedAt}
            onBaselineReplace={replaceFamilyBaseline}
            onFamilyValuesReplace={replaceFamilyValues}
            refetchFamily={refetchFamily}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default CloseoutDefaults;
